import { DocumentAdminLink } from './DocumentAdminAccess';
import React, { useMemo, useState } from 'react';
import AnalyticsChoice from '../../components/common/AnalyticsChoice';
import { analyticsConsent } from '../../lib/analyticsConsent';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { FiChevronLeft, FiRefreshCw, FiAlertCircle, FiBookmark } from 'react-icons/fi';
import { Helmet } from 'react-helmet-async';
import PageLayout from '../../components/layout/PageLayout';
import SEO from '../../components/common/SEO';
import { SITE_URL } from '../../utils/env';
import { aboutApi } from '../../api/about';
import { useSettings } from '../settings/shared';
import baseline from '../../app-ui/baseline';
import copy from './copy.json';
import { documentTypes } from './routes';
import { documentHTML, hasDocument } from './html';
import './Legal.css';
export default function LegalDocument({ type }) {
  const nav = useNavigate();
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const { locale, dark, style, t: common } = useSettings();
  const t = (key, vars = {}) =>
    String(copy[locale === 'en' ? 'en' : 'ko'][key] || key).replace(
      /\{\{(\w+)\}\}/g,
      (_, k) => vars[k] ?? '',
    );
  const title = t('legal.' + type);
  const query = useQuery({
    queryKey: ['document', type, locale],
    queryFn: async ({ signal }) =>
      (await aboutApi.getContent(documentTypes[type], locale, { signal })).data,
    retry: false,
  });
  const html = useMemo(() => documentHTML(query.data?.content), [query.data?.content]);
  const populated = useMemo(() => hasDocument(html), [html]);
  const colors = baseline.tokens[dark ? 'darkColors' : 'lightColors'];
  const refreshLabel = locale === 'en' ? 'Refresh document' : '문서 새로고침';
  return (
    <PageLayout paddingTop={0} paddingBottom={32}>
      <SEO
        title={title}
        description={type === 'about' ? '포스트팝 소개 및 서비스 안내' : undefined}
        url={`${SITE_URL}/about${type === 'terms' ? '/terms-of-use' : type === 'privacy' ? '/privacy-policy' : ''}`}
      />
      {type === 'about' && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Postpop', item: SITE_URL },
                { '@type': 'ListItem', position: 2, name: 'About' },
              ],
            })}
          </script>
        </Helmet>
      )}
      <section
        className="pp-settings pp-legal"
        style={{
          ...style,
          '--legal-primary': colors.primary,
          '--legal-on-primary': colors.onPrimary,
          '--legal-muted': colors.textMuted,
          '--legal-body': colors.reviewBody,
          '--legal-divider-strong': colors.dividerStrong,
          '--legal-skeleton': colors.surfaceMuted,
        }}
        aria-label={title}
      >
        <header className="pp-settings-header">
          <button
            type="button"
            aria-label={common('common.back')}
            onClick={() => (window.history.state?.idx > 0 ? nav(-1) : nav('/mypage/settings'))}
          >
            <FiChevronLeft size={22} />
          </button>
          <h1>{title}</h1>
          <button
            className="pp-legal-refresh"
            type="button"
            disabled={query.isFetching}
            aria-label={refreshLabel}
            onClick={() => query.refetch()}
          >
            <FiRefreshCw size={18} />
          </button>
        </header>
        <DocumentAdminLink />
        {type === 'privacy' && analyticsConsent.enabled && (
          <section className="pp-legal-document">
            <button
              type="button"
              className="pp-legal-analytics-settings"
              onClick={() => setAnalyticsOpen(true)}
            >
              {locale === 'en' ? 'Website analytics settings' : '방문 통계 설정'}
            </button>
            {analyticsOpen && (
              <AnalyticsChoice open onClose={() => setAnalyticsOpen(false)} locale={locale} />
            )}
          </section>
        )}
        {query.isLoading && (
          <div
            className="pp-legal-skeleton"
            role="status"
            aria-label={common('common.loading')}
            aria-busy="true"
          >
            {Array.from({ length: 7 }, (_, i) => (
              <div key={i} />
            ))}
          </div>
        )}
        {query.isError && (
          <div className="pp-legal-state" role="alert">
            <FiAlertCircle size={28} />
            <strong>{common('common.errorTitle')}</strong>
            <p>{t('legal.loadError', { title })}</p>
            <button onClick={() => query.refetch()} disabled={query.isFetching}>
              {common('common.retry')}
            </button>
          </div>
        )}
        {query.data?.fallback && (
          <aside className="pp-legal-notice" role="note">
            {t('legal.fallbackNotice')}
          </aside>
        )}
        {!query.isLoading && !query.isError && !populated && (
          <div className="pp-legal-state">
            <FiBookmark size={28} />
            <strong>{t('legal.emptyTitle')}</strong>
            <p>{t('legal.emptyBody')}</p>
          </div>
        )}
        {populated && (
          <article
            className="pp-legal-document"
            lang={query.data?.locale || (query.data?.fallback ? 'ko' : locale)}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )}
        {type === 'privacy' && (
          <section className="pp-legal-document" aria-labelledby="account-rights-heading">
            <h2 id="account-rights-heading">
              {locale === 'en' ? 'Account deletion and your rights' : '계정 삭제·권리 행사'}
            </h2>
            <Link to={locale === 'en' ? '/account-deletion?lang=en' : '/account-deletion'}>
              {locale === 'en'
                ? 'Delete your Postpop account and data'
                : '포스트팝 계정 및 데이터 삭제 안내'}
            </Link>
          </section>
        )}
      </section>
    </PageLayout>
  );
}
